import * as Sentry from "@sentry/react";
import type { ReactNode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import "./index.css";
import { I18nextProvider } from "react-i18next";
import { App } from "./App";
import { ConsentProvider } from "./context/consent";
import { useCfAnalytics } from "./hooks/use-cf-analytics";
import { useDeepLink } from "./hooks/use-deep-link";
import { useNativeSession } from "./hooks/use-native-session";
import { usePushNotifications } from "./hooks/use-push-notifications";
import { useUtm } from "./hooks/use-utm";
import i18next from "./i18n";
import { ErrorPage } from "./pages/ErrorPage";
import { init as initSentry } from "./sentry.client";

function CfAnalyticsLoader({ children }: { children: ReactNode }) {
	useCfAnalytics();
	return <>{children}</>;
}

function DeepLinkLoader({ children }: { children: ReactNode }) {
	useDeepLink();
	return <>{children}</>;
}

function NativeSessionLoader({ children }: { children: ReactNode }) {
	useNativeSession();
	return <>{children}</>;
}

function PushNotificationLoader({ children }: { children: ReactNode }) {
	usePushNotifications();
	return <>{children}</>;
}

function UtmLoader({ children }: { children: ReactNode }) {
	useUtm();
	return <>{children}</>;
}

initSentry();

const root = document.getElementById("root");
if (root) {
	const appTree = (
		<Sentry.ErrorBoundary
			fallback={({ error, resetError }) => (
				<ErrorPage error={error} resetError={resetError} />
			)}
		>
			<I18nextProvider i18n={i18next}>
				<ConsentProvider>
					<CfAnalyticsLoader>
						<UtmLoader>
							<DeepLinkLoader>
								<NativeSessionLoader>
									<PushNotificationLoader>
										<BrowserRouter>
											<App />
										</BrowserRouter>
									</PushNotificationLoader>
								</NativeSessionLoader>
							</DeepLinkLoader>
						</UtmLoader>
					</CfAnalyticsLoader>
				</ConsentProvider>
			</I18nextProvider>
		</Sentry.ErrorBoundary>
	);

	const sentryOptions = {
		onUncaughtError: (error: unknown) => Sentry.captureException(error),
		onCaughtError: (error: unknown) => Sentry.captureException(error),
		onRecoverableError: (error: unknown) => Sentry.captureException(error),
	};

	if (root.innerHTML.trim().length > 0) {
		hydrateRoot(root, appTree, sentryOptions);
	} else {
		createRoot(root, sentryOptions).render(appTree);
	}
}
